import { useEffect, useMemo, useState } from "react";
import {
  acceptAll,
  rejectAll,
  setConsent,
  getConsent,
  hasDecision,
  CONSENT_EVENTS,
} from "@/utils/cookieConsent";
import { logConsentButton } from "@/utils/nyHemsidaVisit";
import "./CookieConsent.css";

/**
 * Elido Cookie Consent — GDPR/ePrivacy compliant delayed consent modal.
 *
 * Presentation only: the page renders undisturbed, then after exactly 3000 ms
 * a centered fullscreen modal appears as a separate, explicit decision.
 * Consent storage, script gating, attribution and the /ny-hemsida
 * observability funnel are untouched (see utils/cookieConsent.js).
 *
 * Re-opens from the footer via window.dispatchEvent('elido:open-cookie-settings').
 *
 * Categories: Nödvändiga (locked), Statistik (opt-in), Marknadsföring (opt-in).
 * Persistence: localStorage['elido_cookie_consent_v1'].
 */

export const CONSENT_MODAL_DELAY_MS = 3000;

const CATEGORIES = [
  {
    key: "necessary",
    label: "Nödvändiga",
    locked: true,
    desc:
      "Krävs för att webbplatsen ska fungera — inloggning, sidnavigering, och att vi kan komma ihåg ditt cookie-val. Dessa kan inte stängas av.",
  },
  {
    key: "statistics",
    label: "Statistik",
    locked: false,
    desc:
      "Hjälper oss förstå hur besökare använder sidan — t.ex. vilka sidor som läses och hur länge. Data är anonymiserad.",
  },
  {
    key: "marketing",
    label: "Marknadsföring",
    locked: false,
    desc:
      "Används för att visa dig mer relevanta annonser på andra webbplatser (t.ex. Google Ads, Meta). Inga annonser visas här på sidan.",
  },
];

export default function CookieConsent() {
  const initial = useMemo(() => getConsent(), []);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("banner"); // "banner" | "customize"
  const [statistics, setStatistics] = useState(!!initial.statistics);
  const [marketing, setMarketing] = useState(!!initial.marketing);

  // Delayed consent: a saved decision shows nothing. Without a decision the
  // page renders first and the modal opens after exactly 3000 ms. No tracker
  // is started here — gating stays in the consent utility.
  useEffect(() => {
    if (hasDecision()) return undefined;
    const timer = setTimeout(() => setVisible(true), CONSENT_MODAL_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  // Footer link ("Cookieinställningar") dispatches this to re-open the panel.
  useEffect(() => {
    const openFromFooter = () => {
      const current = getConsent();
      setStatistics(!!current.statistics);
      setMarketing(!!current.marketing);
      setMode("customize");
      setVisible(true);
    };
    window.addEventListener(CONSENT_EVENTS.OPEN, openFromFooter);
    return () => window.removeEventListener(CONSENT_EVENTS.OPEN, openFromFooter);
  }, []);

  if (!visible) return null;

  const handleAcceptAll = () => {
    acceptAll();
    logConsentButton("accept_all", { statistics: true, marketing: true });
    setVisible(false);
  };
  const handleRejectAll = () => {
    rejectAll();
    logConsentButton("reject_all", { statistics: false, marketing: false });
    setVisible(false);
  };
  const handleSaveCustom = () => {
    setConsent({ statistics, marketing });
    logConsentButton("custom_save", { statistics, marketing });
    setVisible(false);
  };

  const isCustomize = mode === "customize";

  return (
    <div className="elido-cookie-root" data-testid="cookie-consent-root">
      <div className="elido-cookie-backdrop" data-testid="cookie-consent-backdrop" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
        className={`elido-cookie-sheet${isCustomize ? " elido-cookie-sheet--customize" : ""}`}
        data-testid="cookie-consent-banner"
      >
        <div className="elido-cookie-content">
          {!isCustomize && (
            <div className="elido-cookie-intro">
              <div
                id="cookie-consent-title"
                className="elido-cookie-eyebrow"
                data-testid="cookie-consent-title"
              >
                Cookies
              </div>
              <h2 className="elido-cookie-heading" data-testid="cookie-desktop-heading">
                Vi använder cookies
              </h2>
              <p className="elido-cookie-copy" data-testid="cookie-desktop-description">
                Vi använder valfria cookies för statistik och marknadsföring. Du
                väljer själv vad du vill tillåta.
              </p>
              <p className="elido-cookie-copy elido-cookie-mobile-copy" data-testid="cookie-mobile-description">
                Vi använder valfria cookies för statistik och marknadsföring. Du
                väljer själv vad du vill tillåta.
              </p>
              <a
                href="/integritetspolicy"
                className="elido-cookie-policy-link"
                data-testid="cookie-privacy-link-desktop"
              >
                Läs vår integritetspolicy
              </a>

              <div className="elido-cookie-actions" data-testid="cookie-consent-actions">
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  data-testid="cookie-accept-all"
                  className="elido-cookie-choice elido-cookie-choice--accept"
                >
                  Acceptera alla
                </button>
                <button
                  type="button"
                  onClick={handleRejectAll}
                  data-testid="cookie-reject-all"
                  className="elido-cookie-choice elido-cookie-choice--reject"
                >
                  Neka alla
                </button>
                <button
                  type="button"
                  onClick={() => setMode("customize")}
                  data-testid="cookie-customize-open"
                  className="elido-cookie-choice elido-cookie-choice--tertiary"
                >
                  Anpassa
                </button>
              </div>

              <button
                type="button"
                onClick={() => setMode("customize")}
                className="elido-cookie-mobile-customize"
                data-testid="cookie-customize-open-mobile"
              >
                Anpassa inställningar
              </button>
              <a
                href="/integritetspolicy"
                aria-label="Läs mer om cookies i vår integritetspolicy"
                className="elido-cookie-policy-link elido-cookie-policy-link--mobile"
                data-testid="cookie-privacy-link-mobile"
              >
                Läs mer
              </a>
            </div>
          )}

          {isCustomize && (
            <div className="elido-cookie-customize-panel" data-testid="cookie-customize-panel">
              <div className="elido-cookie-customize-head">
                <div>
                  <div className="elido-cookie-eyebrow">Cookieinställningar</div>
                  <h2
                    id="cookie-consent-title"
                    className="elido-cookie-heading elido-cookie-heading--customize"
                    data-testid="cookie-customize-title"
                  >
                    Anpassa ditt samtycke
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setMode("banner")}
                  className="elido-cookie-back"
                  aria-label="Tillbaka"
                  data-testid="cookie-customize-back"
                >
                  ← Tillbaka
                </button>
              </div>

              <div className="elido-cookie-categories" data-testid="cookie-customize-categories">
                {CATEGORIES.map((cat) => {
                  const isOn =
                    cat.key === "necessary"
                      ? true
                      : cat.key === "statistics"
                      ? statistics
                      : marketing;
                  const onChange = (v) => {
                    if (cat.key === "statistics") setStatistics(v);
                    if (cat.key === "marketing") setMarketing(v);
                  };
                  return (
                    <div
                      key={cat.key}
                      className="elido-cookie-category"
                      data-testid={`cookie-category-${cat.key}`}
                    >
                      <div className="elido-cookie-category-row">
                        <div className="elido-cookie-category-text">
                          <div className="elido-cookie-category-title">
                            <span data-testid={`cookie-category-${cat.key}-label`}>
                              {cat.label}
                            </span>
                            {cat.locked && (
                              <span
                                className="elido-cookie-locked-badge"
                                data-testid="cookie-necessary-always-on"
                              >
                                Alltid på
                              </span>
                            )}
                          </div>
                          <p
                            className="elido-cookie-category-desc"
                            data-testid={`cookie-category-${cat.key}-description`}
                          >
                            {cat.desc}
                          </p>
                        </div>

                        <label
                          className={
                            "elido-cookie-switch" +
                            (cat.locked ? " elido-cookie-switch--locked" : "")
                          }
                          aria-label={`Växla ${cat.label}`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={isOn}
                            disabled={cat.locked}
                            onChange={(e) => onChange(e.target.checked)}
                            data-testid={`cookie-toggle-${cat.key}`}
                          />
                          <span
                            className={
                              "elido-cookie-switch-track" +
                              (isOn ? " elido-cookie-switch-track--on" : "")
                            }
                          />
                          <span
                            className={
                              "elido-cookie-switch-knob" +
                              (isOn ? " elido-cookie-switch-knob--on" : "")
                            }
                          />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="elido-cookie-customize-actions">
                <button
                  type="button"
                  onClick={handleRejectAll}
                  data-testid="cookie-customize-reject"
                  className="elido-cookie-choice elido-cookie-choice--reject"
                >
                  Neka alla
                </button>
                <button
                  type="button"
                  onClick={handleAcceptAll}
                  data-testid="cookie-customize-accept-all"
                  className="elido-cookie-choice elido-cookie-choice--accept"
                >
                  Acceptera alla
                </button>
                <button
                  type="button"
                  onClick={handleSaveCustom}
                  data-testid="cookie-customize-save"
                  className="elido-cookie-choice elido-cookie-choice--save"
                >
                  Spara val
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
